/* Street Garms SG-043 — product-card metadata density experiment.
 * Current/no parameter = Size + Condition (preserves the present site).
 * Test URLs:
 *   ?sg_card=control
 *   ?sg_card=size_condition
 *   ?sg_card=size_colour
 *   ?sg_card=auto   (1/3 session-level random assignment)
 *
 * SG-ANALYTICS-081 adds a narrow GA4 bridge:
 *   - recommended select_item for genuine product-card navigation
 *   - SG-043 and SG-045 experiment events routed to the Street Garms GA4 stream
 * Existing Shopify ecommerce events are left untouched to avoid duplication.
 */
(function () {
  'use strict';

  var EXPERIMENT = 'SG-043';
  var PARAM = 'sg_card';
  var SESSION_KEY = 'sg043CardVariant';
  var LAST_CLICK_KEY = 'sg043LastProductClick';
  var VALID = ['control', 'size_condition', 'size_colour'];
  var GA4_DESTINATION = 'G-MEFN90HZPZ';
  var trackedListGrids = typeof WeakSet === 'function' ? new WeakSet() : null;
  var trackedListGridFallback = [];
  var listObserver = null;
  var listScanScheduled = false;
  var ga4Pending = [];
  var ga4FlushTimer = null;
  var ga4FlushAttempts = 0;

  function normalise(value) {
    value = String(value || '').toLowerCase().trim().replace(/-/g, '_');
    if (value === 'a' || value === 'control' || value === 'current_original') return 'control';
    if (value === 'b' || value === 'size_condition' || value === 'condition') return 'size_condition';
    if (value === 'c' || value === 'size_colour' || value === 'size_color' || value === 'colour' || value === 'color') return 'size_colour';
    if (value === 'auto' || value === 'random') return 'auto';
    return '';
  }

  function randomVariant() {
    return VALID[Math.floor(Math.random() * VALID.length)];
  }

  function getVariant() {
    var requested = '';
    try { requested = normalise(new URL(window.location.href).searchParams.get(PARAM)); } catch (e) {}

    if (requested === 'auto') {
      try {
        var storedAuto = normalise(window.sessionStorage.getItem(SESSION_KEY));
        if (VALID.indexOf(storedAuto) !== -1) return storedAuto;
        var assigned = randomVariant();
        window.sessionStorage.setItem(SESSION_KEY, assigned);
        return assigned;
      } catch (e) {
        return randomVariant();
      }
    }

    if (VALID.indexOf(requested) !== -1) {
      try { window.sessionStorage.setItem(SESSION_KEY, requested); } catch (e) {}
      return requested;
    }

    // Deliberately preserve today's live/current card when no experiment parameter is supplied.
    return 'size_condition';
  }

  function sendGa4Now(eventName, params) {
    if (typeof window.gtag !== 'function') return false;
    try {
      window.gtag('event', eventName, params);
      return true;
    } catch (e) {
      return false;
    }
  }

  function flushGa4Pending() {
    ga4FlushTimer = null;
    if (typeof window.gtag === 'function') {
      var queued = ga4Pending.slice();
      ga4Pending = [];
      ga4FlushAttempts = 0;
      queued.forEach(function (entry) { sendGa4Now(entry.eventName, entry.params); });
      return;
    }

    ga4FlushAttempts += 1;
    if (ga4FlushAttempts >= 20) {
      ga4Pending = [];
      return;
    }
    ga4FlushTimer = window.setTimeout(flushGa4Pending, 250);
  }

  function sendGa4(eventName, params) {
    params = Object.assign({}, params || {}, { send_to: GA4_DESTINATION });
    if (sendGa4Now(eventName, params)) return true;

    ga4Pending.push({ eventName: eventName, params: params });
    if (!ga4FlushTimer) ga4FlushTimer = window.setTimeout(flushGa4Pending, 250);
    return true;
  }

  function scalarDetail(detail) {
    var params = {};
    detail = detail || {};
    Object.keys(detail).forEach(function (key) {
      var value = detail[key];
      if (value === null || value === undefined) return;
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        params[key] = value;
      }
    });
    return params;
  }

  function bindExperimentGa4Bridge() {
    var eventMap = {
      'sg:card-exposure': 'sg043_card_exposure',
      'sg:product-card-click': 'sg043_product_card_click',
      'sg:pdp-view': 'sg043_pdp_view',
      'sg:pdp-add-to-cart': 'sg043_pdp_add_to_cart',
      'sg:sticky-exposure': 'sg045_sticky_exposure',
      'sg:sticky-atc-click': 'sg045_sticky_atc_click',
      'sg:main-pdp-add-to-cart': 'sg045_main_pdp_add_to_cart'
    };

    Object.keys(eventMap).forEach(function (domEventName) {
      window.addEventListener(domEventName, function (event) {
        var detail = event && event.detail ? event.detail : {};
        var params = scalarDetail(detail);
        if (detail.experiment) params.sg_experiment_id = detail.experiment;
        if (detail.variant) {
          if (String(detail.experiment || '').toUpperCase() === 'SG-045') params.sg_sticky_variant = detail.variant;
          else params.sg_card_variant = detail.variant;
        }
        sendGa4(eventMap[domEventName], params);
      });
    });
  }

  function push(eventName, variant, extra) {
    var payload = {
      event: eventName,
      sg_experiment_id: EXPERIMENT,
      sg_card_variant: variant
    };
    if (extra) {
      Object.keys(extra).forEach(function (key) { payload[key] = extra[key]; });
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  function dispatch(eventName, variant, extra) {
    try {
      window.dispatchEvent(new CustomEvent(eventName, {
        detail: Object.assign({ experiment: EXPERIMENT, variant: variant }, extra || {})
      }));
    } catch (e) {}
  }

  function getCardData(card) {
    try { return JSON.parse(card.getAttribute('data-product-options') || '{}'); } catch (e) { return {}; }
  }

  function getCardProduct(card) {
    var data = getCardData(card);
    var link = card.querySelector('a[data-pr-href][href]');
    var title = card.querySelector('.t4s-product-title');
    var rawPrice = Number(data.price);
    return {
      product_id: data.id || '',
      variant_id: data.VariantFirstID || '',
      product_handle: data.handle || '',
      product_url: link ? link.getAttribute('href') : '',
      item_name: title ? String(title.textContent || '').replace(/\s+/g, ' ').trim() : '',
      price: Number.isFinite(rawPrice) ? rawPrice / 100 : null
    };
  }

  function cardIndex(card) {
    var grid = card.closest('.t4s-products');
    if (!grid) return null;
    var cards = Array.prototype.slice.call(grid.querySelectorAll(':scope > .t4s-product, :scope > .t4s-col-item.t4s-product'));
    var index = cards.indexOf(card);
    if (index < 0) {
      cards = Array.prototype.slice.call(grid.querySelectorAll('.t4s-product'));
      index = cards.indexOf(card);
    }
    return index >= 0 ? index : null;
  }

  function cleanHeading(node) {
    return node ? String(node.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  function itemListName(card) {
    if (document.body && document.body.classList.contains('template-search')) return 'Search results';

    if (document.body && document.body.classList.contains('template-collection')) {
      var collectionHeading = document.querySelector('main h1, #MainContent h1, h1');
      var collectionName = cleanHeading(collectionHeading);
      if (collectionName) return collectionName;
    }

    var section = card.closest('.shopify-section, section, [class*="id_"]');
    if (section) {
      var sectionHeading = section.querySelector('.t4s-section-title, .t4s-title, h2, h3');
      var sectionName = cleanHeading(sectionHeading);
      if (sectionName) return sectionName;
    }

    return 'Product list';
  }

  function gaItem(card, product, listName, index) {
    if (!product || !product.product_id || !product.variant_id) return null;
    var item = {
      item_id: 'shopify_GB_' + product.product_id + '_' + product.variant_id,
      item_name: product.item_name || product.product_handle || String(product.product_id),
      item_list_name: listName,
      quantity: 1
    };
    if (index !== null && index !== undefined && index >= 0) item.index = index;
    if (typeof product.price === 'number' && Number.isFinite(product.price)) item.price = product.price;
    return item;
  }

  function sendSelectItem(card, product) {
    if (!product.product_id || !product.variant_id) return;

    var listName = itemListName(card);
    var item = gaItem(card, product, listName, cardIndex(card));
    if (!item) return;

    sendGa4('select_item', {
      item_list_name: listName,
      currency: 'GBP',
      items: [item]
    });
  }

  function listGridAlreadyTracked(grid) {
    if (trackedListGrids) return trackedListGrids.has(grid);
    return trackedListGridFallback.indexOf(grid) !== -1;
  }

  function rememberListGrid(grid) {
    if (trackedListGrids) trackedListGrids.add(grid);
    else trackedListGridFallback.push(grid);
  }

  function shouldSkipListGrid(grid) {
    if (!grid || listGridAlreadyTracked(grid)) return true;
    if (grid.closest && grid.closest('[hidden], .t4s-d-none')) return true;

    // Shopify/Google already sends collection view_item_list events. Do not duplicate them.
    if (document.body && document.body.classList.contains('template-collection')) return true;
    return false;
  }

  function sendListView(grid) {
    if (shouldSkipListGrid(grid)) return;

    var cards = Array.prototype.slice.call(grid.querySelectorAll('.t4s-product')).filter(function (card) {
      return !!card.querySelector('[data-sg043-card-meta]');
    });
    if (!cards.length) return;

    var listName = itemListName(cards[0]);
    var items = cards.map(function (card, index) {
      return gaItem(card, getCardProduct(card), listName, index);
    }).filter(Boolean);
    if (!items.length) return;

    if (sendGa4('view_item_list', {
      item_list_name: listName,
      currency: 'GBP',
      items: items
    })) {
      rememberListGrid(grid);
    }
  }

  function scanListViews() {
    listScanScheduled = false;
    var root = document.getElementById ? (document.getElementById('MainContent') || document.body) : document.body;
    if (!root || !root.querySelectorAll) return;
    Array.prototype.forEach.call(root.querySelectorAll('.t4s-products'), sendListView);
  }

  function scheduleListScan() {
    if (listScanScheduled) return;
    listScanScheduled = true;
    if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(scanListViews);
    else window.setTimeout(scanListViews, 0);
  }

  function bindListViewTracking() {
    // Collection view_item_list events are already owned by Shopify/Google.
    // Skip the redundant DOM observer and full-grid scans on collection pages.
    if (document.body && document.body.classList.contains('template-collection')) return;
    scheduleListScan();
    if (typeof MutationObserver !== 'function') return;
    var root = document.getElementById ? (document.getElementById('MainContent') || document.body) : document.body;
    if (!root) return;
    try {
      listObserver = new MutationObserver(function (mutations) {
        var relevant = mutations.some(function (mutation) { return mutation.addedNodes && mutation.addedNodes.length; });
        if (relevant) scheduleListScan();
      });
      listObserver.observe(root, { childList: true, subtree: true });
    } catch (e) {}
  }

  function bindCardClicks(variant) {
    document.addEventListener('click', function (event) {
      var link = event.target.closest && event.target.closest('.t4s-product a[data-pr-href][href]');
      if (!link) return;
      var card = link.closest('.t4s-product');
      if (!card || !card.querySelector('[data-sg043-card-meta]')) return;

      var product = getCardProduct(card);
      sendSelectItem(card, product);
      push('sg043_product_card_click', variant, product);
      dispatch('sg:product-card-click', variant, product);

      try {
        window.sessionStorage.setItem(LAST_CLICK_KEY, JSON.stringify({
          variant: variant,
          product_id: product.product_id,
          product_handle: product.product_handle,
          product_url: product.product_url,
          timestamp: Date.now()
        }));
      } catch (e) {}
    }, true);
  }

  function readLastClick() {
    try {
      var raw = window.sessionStorage.getItem(LAST_CLICK_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || VALID.indexOf(normalise(data.variant)) === -1) return null;
      if (!data.timestamp || Date.now() - data.timestamp > 30 * 60 * 1000) return null;
      return data;
    } catch (e) {
      return null;
    }
  }

  function trackPdpProgression() {
    if (!document.body || !document.body.classList.contains('template-product')) return;
    var click = readLastClick();
    if (!click) return;

    var variant = normalise(click.variant);
    var path = window.location.pathname || '';
    if (click.product_handle && path.indexOf('/products/' + click.product_handle) === -1) return;

    push('sg043_pdp_view', variant, {
      product_id: click.product_id || '',
      product_handle: click.product_handle || ''
    });
    dispatch('sg:pdp-view', variant, click);

    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
      push('sg043_pdp_add_to_cart', variant, {
        product_id: click.product_id || '',
        product_handle: click.product_handle || ''
      });
      dispatch('sg:pdp-add-to-cart', variant, click);
    }, true);
  }

  function apply() {
    var variant = getVariant();
    document.documentElement.setAttribute('data-sg-card-variant', variant);
    document.documentElement.setAttribute('data-sg-card-experiment', EXPERIMENT);
    document.documentElement.setAttribute('data-sg-ga4-bridge', 'ready');

    var cards = document.querySelectorAll('.t4s-product [data-sg043-card-meta]');
    if (cards.length) {
      push('sg043_card_exposure', variant, { sg_card_count: cards.length });
      dispatch('sg:card-exposure', variant, { cardCount: cards.length });
    }

    // Delegated click tracking stays active for product cards added after initial load.
    bindCardClicks(variant);
    bindListViewTracking();
    trackPdpProgression();
  }

  bindExperimentGa4Bridge();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply, { once: true });
  } else {
    apply();
  }
})();
